import { useState, type FormEvent } from 'react';
import { toErrorMessage } from '../api/client';
import { changeVerificationAlerts } from '../api/endpoints';
import { useToast } from './ToastProvider';

/**
 * "Resolve all open alerts as…" on the verification detail page — SuperAdmin only (the server
 * enforces it too). Twin of the ResolveAlerts form in web/Pages/Verifications/Detail.cshtml.
 */
export function ResolveAlertsForm({ sessionId, onDone }: { sessionId: number; onDone: () => void }) {
  const { showSuccess, showError } = useToast();
  const [status, setStatus] = useState('Resolved');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaving(true);
    try {
      const { changed } = await changeVerificationAlerts(sessionId, { status, notes: notes || null });
      showSuccess(`${changed} fraud alert(s) updated.`);
      setNotes('');
      onDone();
    } catch (err) {
      showError(toErrorMessage(err, 'Could not update the alerts.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="card-footer bg-transparent">
      <form className="row g-2 align-items-end" onSubmit={submit}>
        <div className="col-sm-4">
          <label className="form-label small mb-1" htmlFor="alertStatus">Resolve all open alerts as</label>
          <select id="alertStatus" className="form-select form-select-sm" value={status}
            onChange={(e) => setStatus(e.target.value)}>
            <option value="Resolved">Resolved</option>
            <option value="FalsePositive">False Positive</option>
            <option value="UnderInvestigation">Under Investigation</option>
            <option value="Escalated">Escalated</option>
          </select>
        </div>
        <div className="col-sm-6">
          <label className="form-label small mb-1" htmlFor="alertNotes">Notes</label>
          <input id="alertNotes" className="form-control form-control-sm" maxLength={2000}
            placeholder="Required except for Under Investigation"
            value={notes} onChange={(e) => setNotes(e.target.value)}
            required={status !== 'UnderInvestigation'} />
        </div>
        <div className="col-sm-2">
          <button type="submit" className="btn btn-sm btn-primary w-100" disabled={saving}>
            {saving ? '…' : 'Apply'}
          </button>
        </div>
      </form>
    </div>
  );
}
